// Centralized business info & site constants
export const BUSINESS = {
  name: "Joseph Industries LLC",
  tagline: "Reliable · Professional · Built on Quality",
  region: "Southern Louisiana",
  serviceAreaShort: "New Orleans → Baton Rouge",
  serviceAreaLong: "Serving customers throughout Southern Louisiana — Greater New Orleans, Baton Rouge, Northshore, River Parishes, and surrounding communities",
  phones: [
    { label: "David", display: "(504) 437-6832", tel: "+15044376832" },
    { label: "Josh", display: "(225) 253-3802", tel: "+12252533802" },
  ],
  emails: [
    "david.josephindustries@gmail.com",
    "josephindustriesllc@gmail.com",
  ],
  primaryPhone: { display: "(504) 437-6832", tel: "+15044376832", label: "Call David" },
  secondaryPhone: { display: "(225) 253-3802", tel: "+12252533802", label: "Call Josh" },
  primaryEmail: "david.josephindustries@gmail.com",
  hours: [
    { day: "Mon – Fri", time: "7:00 AM – 7:00 PM" },
    { day: "Saturday", time: "8:00 AM – 5:00 PM" },
    { day: "Sunday", time: "Emergency calls only" },
  ],
  yearsExperience: "20+",
};

// Service pillars — consolidated to 3 top-level offerings.
// Sub-services live as `items` under each pillar so nothing is lost from earlier
// (Repair/Installation merged; Post-Construction, Junk, Assembly, Moving, Heavy Lifting
// all live under "Property & Home Services").
export const PILLARS = [
  {
    key: "appliance-repair-install",
    iconKey: "wrench",
    overline: "Service 01",
    title: "Appliance Repair and Installation",
    short: "Fast repair · clean, code-correct installs",
    desc: "Same-day and next-day service on residential appliances across Southern Louisiana. All major brands — diagnosed, repaired, or newly installed by a licensed crew.",
    image: "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?auto=format&fit=crop&w=1200&q=70",
    items: [
      "Refrigerators & Freezers — repair & install",
      "Washers & Dryers — repair & install",
      "Dishwashers — repair & install",
      "Ranges, Stoves & Ovens (electric or gas)",
      "Cooktops & Over-the-Range Microwaves",
      "Garbage Disposals",
      "Built-in appliances",
      "All major brands · residential & commercial",
    ],
  },
  {
    key: "courier",
    iconKey: "truck",
    overline: "Service 02",
    title: "Courier Services",
    short: "Same-day · business-to-business · medical",
    desc: "Dependable courier and delivery for businesses and individuals across the I-10 corridor and beyond. On-demand or scheduled routes.",
    image: "https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?auto=format&fit=crop&w=1200&q=70",
    items: [
      "Same-day delivery",
      "Scheduled route delivery",
      "Business-to-business courier",
      "Medical supply delivery",
      "Legal document delivery",
      "Parts & equipment delivery",
      "Retail & commercial deliveries",
      "Time-sensitive packages",
    ],
  },
  {
    key: "property-home-services",
    iconKey: "broom",
    overline: "Service 03",
    title: "Property and Home Services",
    short: "Cleanup · junk removal · assembly · moving · heavy lifting",
    desc: "The full property crew — post-construction turnover, junk hauling, furniture & fitness assembly, in-home moves, and the muscle for anything heavy or awkward. One call, one crew, one invoice.",
    image: "https://images.unsplash.com/photo-1581578731548-c64695cc6952?auto=format&fit=crop&w=1200&q=70",
    items: [
      "Post-construction cleanup (dust, debris, final detail)",
      "Junk removal (furniture, appliances, estate cleanouts)",
      "Property & rental cleanouts · garage cleanouts",
      "Furniture & fitness assembly (IKEA, Peloton, home gyms, cribs)",
      "In-home furniture moving · single-item moves · short local moves",
      "Load / unload for rental trucks & storage units",
      "Heavy lifting — safes, pianos, hot tubs, restaurant equipment",
      "Residential & commercial",
    ],
  },
];

export const SERVICE_AREAS = [
  "New Orleans", "Metairie", "Kenner", "Chalmette", "Marrero", "Gretna",
  "LaPlace", "Hammond", "Slidell", "Mandeville", "Covington",
  "Gonzales", "Prairieville", "Baton Rouge", "Denham Springs", "Walker",
  "Houma", "Thibodaux", "Belle Chasse", "Westwego",
];

export const WHY_US = [
  { title: "Licensed & Insured", desc: "Fully credentialed in Louisiana — work guaranteed and protected." },
  { title: "Hassle-Free · No Surprise Fees", desc: "Transparent pricing with a $79 booking fee credited to your final job cost." },
  { title: "Prices Upfront", desc: "All pricing given upfront — before any work is done." },
  { title: "Professional & Reliable", desc: "We show up on time, in uniform, and prepared for the job." },
  { title: "Residential & Commercial", desc: "Homeowners, landlords, property managers, contractors, and businesses." },
  { title: "Fast Response Times", desc: "Most calls scheduled within 24 hours across the corridor." },
  { title: "Quality Workmanship", desc: "Two decades of experience, done to last." },
  { title: "Customer Satisfaction Focused", desc: "We don't leave until it's right — and our work stands behind us." },
];

export const TESTIMONIALS = [
  { quote: "David diagnosed our fridge in 15 minutes and had the part the same day. Saved us a $1,200 replacement.", name: "Megan R.", city: "Metairie, LA" },
  { quote: "Installed our new range and dishwasher cleanly and on time. Honest pricing, no surprises.", name: "Ronald T.", city: "Baton Rouge, LA" },
  { quote: "We use Joseph Industries for our weekly courier runs — never late, professional every trip.", name: "Latoya B.", city: "Gretna, LA" },
];

export const BRANDS = [
  "Whirlpool", "GE", "LG", "Samsung", "Maytag", "KitchenAid", "Frigidaire",
  "Bosch", "Sub-Zero", "Wolf", "Electrolux", "Amana", "Kenmore", "Thermador", "Viking",
];

// Grouped for the Contact form dropdown — same specific options as SERVICE_TYPES,
// organized under the 3 top-level pillars so the customer can see the pillar they belong to.
export const SERVICE_TYPE_GROUPS = [
  {
    label: "Appliance Repair and Installation",
    options: [
      "Appliance Repair — Refrigerator",
      "Appliance Repair — Freezer",
      "Appliance Repair — Washer",
      "Appliance Repair — Dryer",
      "Appliance Repair — Dishwasher",
      "Appliance Repair — Range / Stove / Oven / Cooktop",
      "Appliance Repair — Over-the-Range Microwave",
      "Appliance Repair — Garbage Disposal",
      "Appliance Installation — Refrigerator",
      "Appliance Installation — Dishwasher",
      "Appliance Installation — Range (electric or gas)",
      "Appliance Installation — Washer / Dryer",
      "Appliance Installation — Over-the-Range Microwave",
      "Appliance Installation — Built-in",
    ],
  },
  {
    label: "Courier Services",
    options: [
      "Courier — Same-day",
      "Courier — Scheduled Route",
      "Courier — Business-to-business",
      "Courier — Medical Supply",
      "Courier — Legal Document",
      "Courier — Parts / Equipment",
    ],
  },
  {
    label: "Property and Home Services",
    options: [
      "Post-Construction Cleanup",
      "Junk Removal — Furniture",
      "Junk Removal — Appliances",
      "Junk Removal — Property / Estate Cleanout",
      "Junk Removal — Construction Debris",
      "Assembly — Furniture (flat-pack / IKEA)",
      "Assembly — Fitness Equipment (treadmill / bike / home gym)",
      "Assembly — Crib / Playset / Kids Furniture",
      "Furniture Moving — In-home rearrangement",
      "Furniture Moving — Single item (sofa, piano, safe)",
      "Furniture Moving — Short-distance local move",
      "Furniture Moving — Load / unload rental truck",
      "Heavy Lifting — Safe / Gun safe",
      "Heavy Lifting — Piano",
      "Heavy Lifting — Hot tub / Spa",
      "Heavy Lifting — Commercial / Restaurant equipment",
      "Heavy Lifting — Other awkward load",
    ],
  },
  {
    label: "Other",
    options: ["Other / Not sure"],
  },
];

// Flat list kept for backwards compatibility — some legacy code imports SERVICE_TYPES directly.
export const SERVICE_TYPES = [
  // Appliance Repair
  "Appliance Repair — Refrigerator",
  "Appliance Repair — Freezer",
  "Appliance Repair — Washer",
  "Appliance Repair — Dryer",
  "Appliance Repair — Dishwasher",
  "Appliance Repair — Range / Stove / Oven / Cooktop",
  "Appliance Repair — Over-the-Range Microwave",
  "Appliance Repair — Garbage Disposal",
  // Appliance Installation
  "Appliance Installation — Refrigerator",
  "Appliance Installation — Dishwasher",
  "Appliance Installation — Range (electric or gas)",
  "Appliance Installation — Washer / Dryer",
  "Appliance Installation — Over-the-Range Microwave",
  "Appliance Installation — Built-in",
  // Post-Construction Cleanup
  "Post-Construction Cleanup",
  // Junk Removal
  "Junk Removal — Furniture",
  "Junk Removal — Appliances",
  "Junk Removal — Property / Estate Cleanout",
  "Junk Removal — Construction Debris",
  // Courier
  "Courier — Same-day",
  "Courier — Scheduled Route",
  "Courier — Business-to-business",
  "Courier — Medical Supply",
  "Courier — Legal Document",
  "Courier — Parts / Equipment",
  // Assembly
  "Assembly — Furniture (flat-pack / IKEA)",
  "Assembly — Fitness Equipment (treadmill / bike / home gym)",
  "Assembly — Crib / Playset / Kids Furniture",
  // Furniture Moving
  "Furniture Moving — In-home rearrangement",
  "Furniture Moving — Single item (sofa, piano, safe)",
  "Furniture Moving — Short-distance local move",
  "Furniture Moving — Load / unload rental truck",
  // Heavy Lifting
  "Heavy Lifting — Safe / Gun safe",
  "Heavy Lifting — Piano",
  "Heavy Lifting — Hot tub / Spa",
  "Heavy Lifting — Commercial / Restaurant equipment",
  "Heavy Lifting — Other awkward load",
  "Other / Not sure",
];
